import { useRef, useState } from 'react'
import { CheckCircle2, UploadCloud } from 'lucide-react'
import { Button, Chip } from '@/components/ui'
import type { SaveStatus } from './autosave'

/**
 * Building blocks for long data-entry pages (rendered under
 * data-surface="form"): Products onboarding, SOP editor. Chris 2026-10-09:
 * "fields are tiny, text is small, sections are not separated very well."
 */

export interface SectionStatus {
  missing: number
  warnings: number
  /** True when the section owns checklist items and none is an open error. */
  complete: boolean
}

export function RequiredTag() {
  return <span className="rounded-xs bg-accent-subtle px-1.5 py-px text-xs font-medium text-accent-text">Required</span>
}

/** Label above the control, optional Required tag and helper text. `id` is the jump target (field-<id>). */
export function FormField({
  label,
  required,
  help,
  id,
  className,
  children,
}: {
  label: string
  required?: boolean
  help?: string
  id?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div id={id ? `field-${id}` : undefined}
      className={`-m-1.5 rounded-md p-1.5 transition-shadow data-[flash=true]:shadow-focus ${className ?? ''}`}>
      <label className="flex flex-col gap-1.5">
        <span className="flex flex-wrap items-center gap-2 text-md font-semibold text-primary">
          {label}
          {required ? <RequiredTag /> : null}
        </span>
        {children}
      </label>
      {help ? <p className="mt-1.5 text-sm text-secondary">{help}</p> : null}
    </div>
  )
}

export function SectionStatusChip({ status }: { status: SectionStatus | null }) {
  if (!status) return null
  if (status.missing) return <Chip variant="warning" dot className="text-sm">{status.missing} missing</Chip>
  if (status.warnings) return <Chip variant="info" dot className="text-sm">{status.warnings} to check</Chip>
  return <Chip variant="success" dot className="text-sm">Complete</Chip>
}

/** A numbered, clearly separated card for one step of the form. */
export function StepSection({
  id,
  step,
  title,
  description,
  status,
  children,
}: {
  id: string
  step: number
  title: string
  description?: string
  status?: SectionStatus | null
  children: React.ReactNode
}) {
  return (
    <section id={`section-${id}`} aria-labelledby={`section-${id}-title`}
      className="scroll-mt-4 overflow-clip rounded-lg border border-border-default bg-surface shadow-sm">
      <header className="flex items-center gap-3 border-b border-border-subtle bg-surface-sunk px-6 py-4">
        <span aria-hidden="true"
          className="flex h-[var(--form-step-size)] w-[var(--form-step-size)] shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-text-inverse">
          {step}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={`section-${id}-title`} className="text-xl font-semibold leading-tight text-primary">{title}</h2>
          {description ? <p className="mt-0.5 text-sm text-secondary">{description}</p> : null}
        </div>
        <SectionStatusChip status={status ?? null} />
      </header>
      <div className="flex flex-col gap-5 px-6 py-5">{children}</div>
    </section>
  )
}

/** Plain card for the right-hand panel. */
export function PanelCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border-default bg-surface p-4 shadow-sm">
      <h2 className="text-lg font-semibold text-primary">{title}</h2>
      {children}
    </section>
  )
}

/** Drop a PDF or choose one. Calls onFile with the file; validation stays with the caller. */
export function PdfDropZone({
  onFile,
  busy,
  disabled,
  label,
  hint,
}: {
  onFile: (file: File) => void
  busy?: boolean
  disabled?: boolean
  label: string
  hint?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  return (
    <div
      onDragOver={(e) => {
        if (disabled) return
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        const f = e.dataTransfer.files?.[0]
        if (f && !disabled) onFile(f)
      }}
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center ${
        dragging ? 'border-accent bg-accent-subtle' : 'border-border-strong bg-surface-sunk'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <UploadCloud size={28} className="text-accent" aria-hidden="true" />
      <div className="text-md text-primary">Drag the PDF here, or</div>
      <Button variant="primary" loading={busy} disabled={disabled} onClick={() => input.current?.click()}>
        Choose PDF
      </Button>
      {hint ? <div className="text-sm text-secondary">{hint}</div> : null}
      <input ref={input} type="file" accept="application/pdf,.pdf" className="sr-only" aria-label={label} disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onFile(f)
          e.target.value = ''
        }} />
    </div>
  )
}

/** "On this page": jump links with each section's status. */
export function SectionNav({
  sections,
  onJump,
}: {
  sections: { id: string; step: number; title: string; status: SectionStatus | null }[]
  onJump: (id: string) => void
}) {
  return (
    <nav aria-label="On this page" className="flex flex-col">
      {sections.map((s) => (
        <button key={s.id} type="button" onClick={() => onJump(s.id)}
          className="flex items-center gap-3 rounded-md px-2 py-2 text-left text-md text-primary hover:bg-surface-hover">
          <span className="w-5 text-right text-sm text-tertiary">{s.step}</span>
          <span className="flex-1">{s.title}</span>
          {s.status?.missing ? (
            <span className="text-sm font-medium text-warning-text">{s.status.missing} missing</span>
          ) : s.status ? (
            <CheckCircle2 size={18} className="text-success-text" aria-label="Complete" />
          ) : null}
        </button>
      ))}
    </nav>
  )
}

/** Autosave state for the toolbar: saving / saved / failed. */
export function SaveStatusPill({ status, error }: { status: SaveStatus; error: string | null }) {
  if (status === 'saving') return <Chip variant="neutral">Saving…</Chip>
  if (status === 'error') {
    const friendly = error && / 4\d\d/.test(error)
      ? 'Not saved: a number or date isn\'t valid'
      : 'Not saved yet, retrying'
    return <Chip variant="danger" dot title={error ?? undefined}>{friendly}</Chip>
  }
  if (status === 'saved') return <Chip variant="success" dot>All changes saved</Chip>
  return <Chip variant="neutral">Changes save automatically</Chip>
}
